// Móveis de escritório. GABARITO da Onda 0: 'estacao-trabalho' e 'cadeira-escritorio'.
// O lote da H5 (mesas, monitores, cadeiras, estantes, arquivo, impressora, plantas) segue as mesmas regras.
//
// Como ler: cada móvel é um Pix (ver ../pix.ts) com letras de paleta locais. Luz do
// alto-esquerda: tom 3 no topo e à esquerda, tom 1 embaixo e à direita, tom 0 no contorno.
// Em 3/4 o tampo de mesa aparece de cima (faixa larga) e a frente aparece como faixa
// mais escura embaixo. Tudo que é alto (monitor, encosto) sobe acima da pegada.

import { Pix } from '../pix.js';
import type { FurnitureDef } from '../types.js';

const WOOD = { o: 'wood0', D: 'wood1', W: 'wood2', L: 'wood3' };

/** Tampo visto de cima (faixa clara), frente escura de 2 px e dois pés. */
function table(pix: Pix, x: number, y: number, w: number, d: number, legH: number): Pix {
  pix.box(x, y, w, d, { fill: 'W', light: 'L', dark: 'D' });
  pix.rect(x, y + d, w, 2, 'D');
  pix.rect(x + 1, y + d + 2, 2, legH, 'D').rect(x + w - 3, y + d + 2, 2, legH, 'D');
  return pix;
}

function officeChair(): Pix {
  const pix = new Pix(16, 22);
  // encosto (fica atrás de quem senta): mais largo que o personagem, para aparecer dos lados
  pix.box(1, 0, 14, 11, { fill: 'c', light: 'l', dark: 'd' });
  pix.hline(3, 2, 10, 'l');
  // assento
  pix.box(2, 10, 12, 5, { fill: 'c', light: 'l', dark: 'd' });
  // coluna e base em estrela
  pix.rect(7, 15, 2, 3, 'm').hline(3, 18, 10, 'm').hline(2, 19, 3, 'n').hline(11, 19, 3, 'n');
  pix.rect(2, 20, 2, 1, 'k').rect(12, 20, 2, 1, 'k').rect(7, 20, 2, 1, 'k');
  return pix.outline('o');
}

/** Mesa de 2 tiles com monitor de frente para a câmera: a tela aparece para quem olha, e o agente senta do lado de baixo, de costas. */
function workstation(): Pix {
  const pix = new Pix(32, 28);
  table(pix, 0, 15, 32, 8, 3);
  // pé e base do monitor; moldura preta e tela (a animação é pintada em cima, ver STATION_SCREENS)
  pix.rect(14, 13, 4, 2, 'z').hline(11, 14, 10, 'z');
  pix.box(8, 0, 16, 13, { fill: 'k', light: 'h', dark: 'z' });
  pix.rect(10, 2, 12, 8, 's');
  pix.hline(12, 3, 4, 'g').hline(12, 5, 7, 'g').hline(12, 7, 3, 'G');
  // teclado, mouse, papel e caneca sobre o tampo
  pix.rect(9, 17, 14, 3, 'k').pattern(10, 18, 12, 'K.');
  pix.rect(25, 17, 2, 3, 'p');
  pix.rect(2, 17, 5, 4, 'p').hline(3, 18, 3, 'q');
  pix.box(28, 17, 3, 4, { fill: 'r', light: 'R' });
  return pix.outline('o');
}

/** Estação com monitor maior, de frente para a câmera: a tela fica voltada para quem olha. */
function stationFront(): Pix {
  const pix = new Pix(32, 29);
  table(pix, 0, 15, 32, 8, 3);
  // monitor com pé e base (a tela é pintada em cima, ver STATION_SCREENS)
  pix.box(7, 0, 18, 13, { fill: 'k', light: 'h', dark: 'z' });
  pix.rect(9, 2, 14, 9, 's');
  pix.hline(10, 3, 5, 'g').hline(16, 3, 3, 'G');
  pix.hline(12, 5, 9, 'g');
  pix.hline(10, 7, 4, 'g').hline(15, 7, 5, 'G');
  pix.hline(10, 9, 2, 'g');
  pix.rect(14, 13, 4, 1, 'z').hline(11, 14, 10, 'z');
  // teclado, mouse, papel e caneca sobre o tampo
  pix.rect(8, 17, 14, 3, 'k').pattern(9, 18, 12, 'K.');
  pix.rect(24, 17, 2, 3, 'p');
  pix.rect(2, 17, 5, 4, 'p').hline(3, 18, 3, 'q');
  pix.box(27, 17, 3, 4, { fill: 'r', light: 'R' });
  return pix.outline('o');
}

const FRONT_PALETTE = {
  ...WOOD,
  k: 'black1',
  h: 'black3',
  z: 'black0',
  s: 'screen0',
  g: 'screen2',
  G: 'screen3',
  K: 'metal2',
  p: 'paper3',
  q: 'paper1',
  r: 'red2',
  R: 'red3',
};

/** Notebook sobre a mesa, com a tela da tampa voltada para a câmera (o agente senta do lado de baixo, de costas). */
function laptopDesk(): Pix {
  const pix = new Pix(32, 29);
  table(pix, 0, 16, 32, 7, 3);
  // tampa de pé, com moldura: a tela (animada, ver STATION_SCREENS) e uma webcam
  pix.box(7, 1, 18, 14, { fill: 'b', light: 'c', dark: 'a' });
  pix.rect(9, 3, 14, 9, 's');
  pix.hline(10, 4, 4, 'g').hline(10, 6, 8, 'g');
  pix.px(15, 2, 'a');
  // base do notebook sobre a mesa e caneca
  pix.box(5, 15, 22, 4, { fill: 'b', light: 'c', dark: 'a' });
  pix.pattern(8, 17, 16, 'a.');
  pix.box(25, 17, 3, 4, { fill: 'r', light: 'R' });
  return pix.outline('o');
}

const LAPTOP_PALETTE = { ...WOOD, a: 'plastic1', b: 'plastic2', c: 'plastic3', s: 'screen0', g: 'screen2', r: 'red2', R: 'red3' };

/** Dois monitores de frente para a câmera: as duas telas aparecem, e o agente senta do lado de baixo da mesa. */
function twoMonitors(): Pix {
  const pix = new Pix(32, 29);
  table(pix, 0, 13, 32, 8, 3);
  for (const x of [1, 17]) {
    pix.box(x, 0, 14, 11, { fill: 'k', light: 'h', dark: 'z' });
    pix.rect(x + 1, 1, 12, 8, 's');
    pix.hline(x + 2, 3, 6, 'g').hline(x + 2, 5, 9, 'g');
    pix.rect(x + 5, 11, 4, 2, 'z');
  }
  // teclado e mouse
  pix.rect(9, 15, 14, 3, 'k').pattern(10, 16, 12, 'K.');
  pix.rect(26, 15, 2, 3, 'p');
  return pix.outline('o');
}

const TWO_MONITOR_PALETTE = { ...WOOD, k: 'black1', h: 'black3', z: 'black0', s: 'screen0', g: 'screen2', K: 'metal2', p: 'paper3' };

const PLAIN_PALETTE = { ...WOOD, p: 'paper3', q: 'paper1', r: 'red2', R: 'red3' };

const BACK_PALETTE = { ...WOOD, m: 'metal2', M: 'metal3', d: 'metal1', p: 'paper3', q: 'paper1', r: 'red2', R: 'red3' };

const DIVIDER_PALETTE = { o: 'black0', c: 'fabricGray2', l: 'fabricGray3', d: 'fabricGray1', L: 'fabricGray3' };

/** Mesa simples de madeira, com papéis e caneca. Sem monitor. */
function plainDesk(): Pix {
  const pix = new Pix(32, 22);
  table(pix, 0, 4, 32, 9, 6);
  pix.rect(4, 6, 6, 4, 'p').hline(5, 7, 4, 'q');
  pix.box(25, 6, 3, 4, { fill: 'r', light: 'R' });
  return pix.outline('o');
}

/** Mesa da diretoria (3 tiles): tampo escuro, sousplat preto e placa dourada. */
function executiveDesk(): Pix {
  const pix = new Pix(48, 28);
  table(pix, 0, 6, 48, 12, 6);
  pix.box(6, 8, 24, 8, { fill: 'k', light: 'K', dark: 'z' });
  pix.box(34, 9, 9, 4, { fill: 'n', light: 'N', dark: 'g' });
  return pix.outline('o');
}

const EXEC_PALETTE = { ...WOOD, k: 'black1', K: 'black2', z: 'black0', n: 'gold2', N: 'gold3', g: 'gold1' };

/** Cadeira gamer vista de trás do encosto: encosto alto, laterais e base em estrela. */
function gamerChair(): Pix {
  const pix = new Pix(16, 24);
  pix.box(3, 0, 10, 12, { fill: 'R', light: 'Q', dark: 'x' });
  pix.rect(1, 3, 2, 8, 'k').rect(13, 3, 2, 8, 'k');
  pix.box(2, 12, 12, 5, { fill: 'R', light: 'Q', dark: 'x' });
  pix.rect(6, 17, 4, 3, 'K');
  pix.hline(3, 20, 10, 'K').hline(2, 21, 3, 'k').hline(11, 21, 3, 'k');
  return pix.outline('o');
}

/**
 * Cadeira escura com o encosto virado para a câmera, na frente de quem senta do lado de baixo
 * da mesa. O encosto é baixo: o tronco e a cabeça aparecem por cima dele, e o assento cobre o quadril.
 */
function chairFront(): Pix {
  const pix = new Pix(16, 16);
  pix.box(3, 7, 10, 6, { fill: 'c', light: 'l', dark: 'd' });
  // assento, mais largo que o encosto
  pix.box(1, 12, 14, 3, { fill: 'c', light: 'l', dark: 'd' });
  pix.rect(7, 15, 2, 1, 'm');
  return pix.outline('o');
}

/** Monitor de costas, numa mesa de quem senta do lado de cima: traseira cinza com o pé, sem tela. */
function stationBack(): Pix {
  const pix = new Pix(32, 29);
  table(pix, 0, 15, 32, 8, 3);
  // traseira do monitor (a tela fica voltada para quem senta, do outro lado)
  pix.box(7, 0, 18, 13, { fill: 'm', light: 'M', dark: 'd' });
  pix.rect(14, 13, 4, 2, 'd').hline(11, 14, 10, 'd');
  // papel e caneca, do lado da câmera
  pix.rect(2, 17, 5, 4, 'p').hline(3, 18, 3, 'q');
  pix.box(27, 17, 3, 4, { fill: 'r', light: 'R' });
  return pix.outline('o');
}

/** Divisória baixa (meio tile) entre duas estações: painel de tecido cinza, com faixa mais clara no topo. */
function divider(): Pix {
  // largura cheia: as peças emendam e formam um muro baixo contínuo (só o topo e a base têm contorno)
  const pix = new Pix(16, 8);
  pix.box(0, 1, 16, 6, { fill: 'c', light: 'l', dark: 'd' });
  pix.hline(0, 2, 16, 'L');
  return pix.outline('o');
}

/**
 * Estante de 1 tile: corpo de madeira, 4 compartimentos com livros em faixas coloridas.
 * `books` tem uma letra por livro (2 px de largura), usada em ciclo em cada compartimento.
 */
function bookcase(pix: Pix, x0: number, w: number, books: string): Pix {
  pix.box(x0, 0, w, 31, { fill: 'W', light: 'L', dark: 'D' });
  pix.rect(x0 + 2, 1, w - 4, 29, 'D'); // fundo do armário
  const compartments: Array<[number, number]> = [[1, 7], [10, 15], [18, 23], [26, 29]];
  compartments.forEach(([y0, y1], r) => {
    for (let x = x0 + 2; x < x0 + w - 2; x++) {
      const c = books[(Math.floor((x - x0 - 2) / 2) + r) % books.length] ?? 'e';
      pix.vline(x, y0 + (x % 3 === 0 ? 1 : 0), y1 - y0 + (x % 3 === 0 ? 0 : 1), c);
    }
  });
  for (const by of [8, 16, 24]) pix.rect(x0 + 1, by, w - 2, 2, 'W').hline(x0 + 1, by, w - 2, 'L');
  return pix;
}

const SHELF_PALETTE = {
  ...WOOD,
  a: 'red2',
  b: 'fabricBlue2',
  c: 'fabricGreen2',
  y: 'fabricMustard2',
  e: 'paper2',
  f: 'fabricGray2',
  h: 'fabricBlue3',
};

/** Gaveteiro de metal com 3 gavetas e puxadores. */
function filingCabinet(): Pix {
  const pix = new Pix(16, 23);
  pix.box(1, 0, 14, 21, { fill: 'm', light: 'M', dark: 'd' });
  pix.hline(2, 7, 12, 'd').hline(2, 8, 12, 'M');
  pix.hline(2, 14, 12, 'd').hline(2, 15, 12, 'M');
  pix.rect(6, 4, 4, 1, 'k').rect(6, 11, 4, 1, 'k').rect(6, 18, 4, 1, 'k');
  pix.rect(2, 21, 2, 1, 'k').rect(12, 21, 2, 1, 'k');
  return pix.outline('o');
}

/** Impressora de mesa a laser: corpo, saída de papel e LED. */
function printer(): Pix {
  const pix = new Pix(16, 18);
  pix.rect(4, 1, 8, 5, 'e').hline(4, 1, 8, 'q');
  pix.box(1, 6, 14, 8, { fill: 'P', light: 'Q', dark: 'p' });
  pix.hline(3, 11, 10, 'k');
  pix.px(12, 8, 'r');
  pix.rect(2, 14, 2, 2, 'k').rect(12, 14, 2, 2, 'k');
  return pix.outline('o');
}

/** Bebedouro de galão: garrafa de água em cima, corpo branco, torneira. */
function waterCooler(): Pix {
  const pix = new Pix(16, 30);
  pix.rect(5, 1, 6, 2, 'k');
  pix.box(4, 3, 8, 10, { fill: 'X', light: 'y', dark: 'x' });
  pix.box(2, 13, 12, 14, { fill: 'b', light: 'c', dark: 'a' });
  pix.rect(6, 19, 4, 2, 'k').rect(7, 21, 2, 2, 'k');
  pix.hline(3, 27, 10, 'a');
  return pix.outline('o');
}

/** Lixeira de metal com tampa e faixas. */
function bin(): Pix {
  const pix = new Pix(16, 18);
  pix.rect(6, 1, 4, 2, 'd');
  pix.box(2, 3, 12, 2, { fill: 'M', light: 'M', dark: 'd' });
  pix.box(3, 5, 10, 12, { fill: 'm', light: 'M', dark: 'd' });
  pix.hline(4, 8, 8, 'd').hline(4, 11, 8, 'd');
  return pix.outline('o');
}

/** Planta pequena em vaso de barro. */
function smallPlant(): Pix {
  const pix = new Pix(16, 24);
  pix.disc(8, 8, 6, 'd').disc(6, 6, 4, 'g').disc(10, 9, 3, 'g').disc(6, 5, 2, 'G');
  pix.box(4, 16, 8, 6, { fill: 'q', light: 'r', dark: 'p' });
  pix.box(3, 13, 10, 3, { fill: 'q', light: 'r', dark: 'p' });
  return pix.outline('o');
}

/** Planta alta com folhagem em vários tons e vaso grande. */
function bigPlant(): Pix {
  const pix = new Pix(16, 36);
  pix.disc(8, 5, 4, 'd').disc(8, 9, 7, 'd').disc(4, 17, 5, 'd').disc(12, 17, 5, 'd');
  pix.disc(6, 8, 4, 'g').disc(3, 16, 3, 'g').disc(11, 15, 3, 'g').disc(5, 4, 2, 'G');
  pix.rect(7, 22, 2, 4, 'p');
  pix.box(2, 25, 12, 3, { fill: 'q', light: 'r', dark: 'p' });
  pix.box(3, 28, 10, 6, { fill: 'q', light: 'r', dark: 'p' });
  return pix.outline('o');
}

/** Quadro branco com moldura de metal, bandeja de canetas e rodinhas. */
function whiteboard(): Pix {
  const pix = new Pix(32, 30);
  pix.box(1, 0, 30, 19, { fill: 'm', light: 'M', dark: 'd' });
  pix.box(3, 2, 26, 14, { fill: 'w', light: 'W', dark: 'a' });
  pix.hline(6, 5, 10, 'r').hline(6, 8, 14, 'b').hline(6, 11, 7, 'k');
  pix.rect(6, 17, 20, 2, 'm').hline(6, 17, 20, 'M');
  pix.rect(8, 16, 3, 1, 'r').rect(14, 16, 3, 1, 'b');
  pix.rect(4, 19, 2, 8, 'd').rect(26, 19, 2, 8, 'd');
  pix.hline(4, 25, 24, 'd');
  pix.rect(3, 27, 4, 2, 'k').rect(25, 27, 4, 2, 'k');
  return pix.outline('o');
}

/** Luminária de chão: cúpula de tecido, haste de metal e base. Brilho quente à noite. */
function floorLamp(): Pix {
  const pix = new Pix(16, 36);
  pix.box(3, 2, 10, 7, { fill: 'f', light: 'F', dark: 'd' });
  pix.hline(5, 9, 6, 'B');
  pix.vline(7, 10, 22, 'm').vline(8, 10, 22, 'M');
  pix.box(3, 31, 10, 3, { fill: 'm', light: 'M', dark: 'k' });
  return pix.outline('o');
}

/** Duas caixas de papelão, uma sobre a outra no canto: fita e etiqueta. */
function cardboardBoxes(): Pix {
  const pix = new Pix(32, 26);
  pix.box(1, 10, 18, 14, { fill: 'W', light: 'L', dark: 'D' });
  pix.rect(8, 10, 3, 14, 'e');
  pix.rect(12, 15, 4, 4, 'p').hline(12, 15, 4, 'q');
  pix.box(19, 3, 12, 11, { fill: 'W', light: 'L', dark: 'D' });
  pix.rect(23, 3, 2, 11, 'e');
  return pix.outline('o');
}

/** Cabideiro de pé com um casaco pendurado. */
function coatRack(): Pix {
  const pix = new Pix(16, 30);
  pix.vline(7, 6, 21, 'm').vline(8, 6, 21, 'M');
  pix.hline(2, 5, 12, 'M').hline(2, 6, 12, 'm');
  // casaco pendurado no cabide: ombros arredondados e barra da gola
  pix.box(3, 8, 10, 12, { fill: 'b', light: 'c', dark: 'a' });
  pix.px(3, 8, '.').px(12, 8, '.').px(3, 19, '.').px(12, 19, '.');
  pix.hline(6, 8, 4, 'c');
  pix.box(2, 26, 12, 3, { fill: 'm', light: 'M', dark: 'd' });
  return pix.outline('o');
}

/**
 * Telas dos monitores de cada estação, em px dentro do sprite (x, y, w, h). É nelas que
 * src/web/engine/scene.ts pinta a animação do que o agente está fazendo.
 */
export const STATION_SCREENS: Readonly<Record<string, readonly { x: number; y: number; w: number; h: number }[] | undefined>> = {
  'estacao-trabalho': [{ x: 10, y: 2, w: 12, h: 8 }],
  'estacao-trabalho-costas': [{ x: 9, y: 2, w: 14, h: 9 }],
  'estacao-notebook': [{ x: 9, y: 3, w: 14, h: 9 }],
  'estacao-dois-monitores': [
    { x: 2, y: 1, w: 12, h: 8 },
    { x: 18, y: 1, w: 12, h: 8 },
  ],
};

export const OFFICE_FURNITURE: readonly FurnitureDef[] = [
  {
    id: 'estacao-trabalho',
    name: 'Estação de trabalho',
    category: 'office',
    footprint: { w: 2, h: 1 },
    sprite: workstation().sprite(FRONT_PALETTE),
    // agente do lado de baixo da mesa (tile de baixo, à direita), virado para cima: a tela fica para a câmera
    spots: [{ dx: 1, dy: 1, facing: 'up', kind: 'desk', use: 'work' }],
  },
  {
    id: 'estacao-trabalho-costas',
    name: 'Estação com tela de frente',
    category: 'office',
    footprint: { w: 2, h: 1 },
    sprite: stationFront().sprite(FRONT_PALETTE),
    // mesmo lado das outras estações com tela: embaixo da mesa, virado para cima
    spots: [{ dx: 1, dy: 1, facing: 'up', kind: 'desk', use: 'work' }],
  },
  {
    id: 'estacao-notebook',
    name: 'Notebook na mesa',
    category: 'office',
    footprint: { w: 2, h: 1 },
    sprite: laptopDesk().sprite(LAPTOP_PALETTE),
    spots: [{ dx: 1, dy: 1, facing: 'up', kind: 'desk', use: 'work' }],
  },
  {
    id: 'estacao-dois-monitores',
    name: 'Estação com dois monitores',
    category: 'office',
    footprint: { w: 2, h: 1 },
    sprite: twoMonitors().sprite(TWO_MONITOR_PALETTE),
    spots: [{ dx: 1, dy: 1, facing: 'up', kind: 'desk', use: 'work' }],
  },
  {
    id: 'mesa-simples',
    name: 'Mesa simples',
    category: 'office',
    footprint: { w: 2, h: 1 },
    sprite: plainDesk().sprite(PLAIN_PALETTE),
  },
  {
    id: 'mesa-diretor',
    name: 'Mesa da diretoria',
    category: 'office',
    footprint: { w: 3, h: 1 },
    sprite: executiveDesk().sprite(EXEC_PALETTE),
  },
  {
    id: 'cadeira-gamer',
    name: 'Cadeira gamer',
    category: 'office',
    footprint: { w: 1, h: 1 },
    sprite: gamerChair().sprite({ o: 'black0', R: 'red2', Q: 'red3', x: 'red0', k: 'black1', K: 'black2' }),
    sortBias: -4,
    blocks: false,
  },
  {
    id: 'cadeira-escritorio',
    name: 'Cadeira de escritório',
    category: 'office',
    footprint: { w: 1, h: 1 },
    sprite: officeChair().sprite({ o: 'black0', d: 'fabricGray1', c: 'fabricGray2', l: 'fabricGray3', m: 'metal1', n: 'metal2', k: 'black1' }),
    sortBias: -4,
    blocks: false,
  },
  {
    id: 'cadeira-escritorio-costas',
    name: 'Cadeira escura com encosto de frente',
    category: 'office',
    footprint: { w: 1, h: 1 },
    sprite: chairFront().sprite({ o: 'black0', c: 'fabricGray1', l: 'fabricGray2', d: 'fabricGray0', m: 'metal1' }),
    // desenha por cima de quem senta: o encosto fica na frente
    sortBias: 6,
    blocks: false,
  },
  {
    id: 'estacao-costas',
    name: 'Estação com monitor de costas',
    category: 'office',
    footprint: { w: 2, h: 1 },
    sprite: stationBack().sprite(BACK_PALETTE),
    // agente do lado de cima da mesa, virado para baixo: a traseira do monitor fica para a câmera
    spots: [{ dx: 1, dy: -1, facing: 'down', kind: 'desk', use: 'work' }],
  },
  {
    id: 'divisoria-baixa',
    name: 'Divisória baixa',
    category: 'office',
    footprint: { w: 1, h: 1 },
    sprite: divider().sprite(DIVIDER_PALETTE),
  },
  {
    id: 'estante-livros',
    name: 'Estante de livros',
    category: 'office',
    footprint: { w: 1, h: 1 },
    sprite: bookcase(new Pix(16, 32), 0, 16, 'acyefh').sprite(SHELF_PALETTE),
  },
  {
    id: 'estante-dupla',
    name: 'Estante dupla',
    category: 'office',
    footprint: { w: 2, h: 1 },
    sprite: bookcase(bookcase(new Pix(32, 32), 0, 16, 'acyefh'), 16, 16, 'bfacey').sprite(SHELF_PALETTE),
  },
  {
    id: 'gaveteiro',
    name: 'Gaveteiro de metal',
    category: 'office',
    footprint: { w: 1, h: 1 },
    sprite: filingCabinet().sprite({ o: 'black0', d: 'metal1', m: 'metal2', M: 'metal3', k: 'black1' }),
  },
  {
    id: 'impressora',
    name: 'Impressora',
    category: 'office',
    footprint: { w: 1, h: 1 },
    sprite: printer().sprite({ o: 'black0', p: 'plastic1', P: 'plastic2', Q: 'plastic3', k: 'black1', e: 'paper3', q: 'paper1', r: 'red2' }),
  },
  {
    id: 'bebedouro',
    name: 'Bebedouro',
    category: 'office',
    footprint: { w: 1, h: 1 },
    sprite: waterCooler().sprite({ o: 'black0', a: 'white1', b: 'white2', c: 'white3', x: 'water1', X: 'water2', y: 'water3', k: 'black1' }),
  },
  {
    id: 'lixeira',
    name: 'Lixeira',
    category: 'decor',
    footprint: { w: 1, h: 1 },
    sprite: bin().sprite({ o: 'black0', d: 'metal1', m: 'metal2', M: 'metal3' }),
  },
  {
    id: 'planta-vaso',
    name: 'Planta de vaso',
    category: 'decor',
    footprint: { w: 1, h: 1 },
    sprite: smallPlant().sprite({ o: 'leaf0', d: 'leaf1', g: 'leaf2', G: 'leaf3', p: 'pot1', q: 'pot2', r: 'pot3' }),
  },
  {
    id: 'planta-grande',
    name: 'Planta grande',
    category: 'decor',
    footprint: { w: 1, h: 1 },
    sprite: bigPlant().sprite({ o: 'leaf0', d: 'leaf1', g: 'leaf2', G: 'leaf3', p: 'pot1', q: 'pot2', r: 'pot3' }),
  },
  {
    id: 'quadro-branco',
    name: 'Quadro branco com rodinhas',
    category: 'office',
    footprint: { w: 2, h: 1 },
    sprite: whiteboard().sprite({ o: 'black0', d: 'metal1', m: 'metal2', M: 'metal3', w: 'white2', W: 'white3', a: 'white1', r: 'red2', b: 'fabricBlue2', k: 'black1' }),
  },
  {
    id: 'luminaria-chao',
    name: 'Luminária de chão',
    category: 'decor',
    footprint: { w: 1, h: 1 },
    sprite: floorLamp().sprite({ o: 'black0', f: 'fabricMustard2', F: 'fabricMustard3', d: 'fabricMustard1', B: 'light3', m: 'metal2', M: 'metal3', k: 'black1' }),
    glow: { color: 'rgba(255, 220, 140, 0.4)', radius: 30, dy: -12 },
  },
  {
    id: 'caixas-papelao',
    name: 'Caixas de papelão',
    category: 'decor',
    footprint: { w: 2, h: 1 },
    sprite: cardboardBoxes().sprite({ o: 'oak0', D: 'oak1', W: 'oak2', L: 'oak3', e: 'paper2', p: 'paper3', q: 'paper1' }),
  },
  {
    id: 'cabideiro',
    name: 'Cabideiro',
    category: 'decor',
    footprint: { w: 1, h: 1 },
    sprite: coatRack().sprite({ o: 'black0', m: 'metal2', M: 'metal3', d: 'metal1', b: 'fabricGreen2', c: 'fabricGreen3', a: 'fabricGreen1' }),
  },
];
